import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import * as activityActions from "@rentiohq/shared-frontend/dist/redux/activity/activity.actions";
import * as activitySelectors from "@rentiohq/shared-frontend/dist/redux/activity/activity.selectors";
import { useSelf } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as countSelectors from "@rentiohq/shared-frontend/dist/redux/count/count.selectors";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { IPagedQuery } from "@rentiohq/shared-frontend/dist/reduxV2/utils/api.types";
import {
  ACTIVITY_SUBTYPES,
  IActivityItem,
} from "@rentiohq/shared-frontend/dist/types/activity.types";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { endOfDay } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { useTheme, useMediaQuery } from "@rentiohq/web-shared-next/dist/themes";
import { Page, Stack } from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { compact } from "lodash";
import hash from "object-hash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { OpenPaymentRequests } from "./components/OpenPaymentRequests";
import { PaymentHistory } from "./components/PaymentHistory";
import { PaymentHistoryTable } from "./components/PaymentHistoryTable";
import { UpcomingPaymentRequests } from "./components/UpcomingPaymentRequests";

const NOW = new Date();
const END_OF_DAY = endOfDay(NOW);

export const PaymentsContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelf();

  const theme = useTheme();

  const downLG = useMediaQuery(theme.breakpoints.down("lg"));

  const paymentActivitiesIdentifier = `payment-history-activities-${hash(
    getActivitiesFilter(),
  )}`;

  const [historyPage, setHistoryPage] = React.useState<number>(1);
  const [openPaymentsPage, setOpenPaymentsPage] = React.useState<number>(1);
  const [upcomingPaymentsPage, setUpcomingPaymentsPage] =
    React.useState<number>(1);

  const getOpenPaymentsFilter = () => {
    return {
      limit: CONFIG.DEFAULT_FETCH_LIMIT,
      page: openPaymentsPage,
      sort: [
        { field: "dueDateAt", method: "ASC" },
        { field: "id", method: "ASC" },
      ],
      filter: {
        requestPayerAccountId: { in: user?.accountIds },
        status: {
          in: [EPaymentRequestStatus.New, EPaymentRequestStatus.FailedPaidIn],
        },
        startedAt: { lte: NOW },
      },
    };
  };

  const getUpcomingPaymentsFilter = () => {
    return {
      limit: CONFIG.DEFAULT_FETCH_LIMIT,
      page: upcomingPaymentsPage,
      sort: [
        { field: "dueDateAt", method: "ASC" },
        { field: "id", method: "ASC" },
      ],
      filter: {
        requestPayerAccountId: { in: user?.accountIds },
        status: {
          in: [EPaymentRequestStatus.New, EPaymentRequestStatus.FailedPaidIn],
        },
        startedAt: { gt: END_OF_DAY },
      },
    };
  };

  const {
    items: openPaymentRequests = [],
    isFetching: isFetchingOpenPaymentRequests,
    fetchError: openPaymentRequestsFetchError,
  } = paymentRequestHooks.usePaged({
    shouldRefetch: true,
    query: getOpenPaymentsFilter() as IPagedQuery,
  });

  const {
    items: upcomingPaymentRequests = [],
    isFetching: isFetchingUpcomingPaymentRequests,
    fetchError: upcomingPaymentRequestsFetchError,
  } = paymentRequestHooks.usePaged({
    shouldRefetch: true,
    query: getUpcomingPaymentsFilter() as IPagedQuery,
  });

  const { count: openPaymentRequestsCount = 0 } = paymentRequestHooks.useCount({
    shouldRefetch: true,
    query: getOpenPaymentsFilter(),
  });

  const { count: upcomingPaymentRequestsCount = 0 } =
    paymentRequestHooks.useCount({
      shouldRefetch: true,
      query: getUpcomingPaymentsFilter(),
    });

  const isFetchingActivities = useSelector((state: IRootStore) =>
    activitySelectors.isFetchingActivitiesForPage(
      state,
      paymentActivitiesIdentifier,
      historyPage,
    ),
  );

  const fetchErrorActivities = useSelector((state: IRootStore) =>
    activitySelectors.activitiesForPageFetchError(
      state,
      paymentActivitiesIdentifier,
      historyPage,
    ),
  );

  const activities = useSelector((state: IRootStore) =>
    activitySelectors.getActivitiesPaged(
      state,
      paymentActivitiesIdentifier,
      historyPage,
    ),
  );

  const activitiesCount = useSelector((state: IRootStore) =>
    countSelectors.getCount(state, `activities-${paymentActivitiesIdentifier}`),
  );

  React.useEffect(() => {
    if (isFetchingActivities) {
      return;
    }

    dispatch(
      activityActions.getActivitiesPaged.actions.start({
        limit: CONFIG.DEFAULT_FETCH_LIMIT,
        page: historyPage,
        identifier: paymentActivitiesIdentifier,
        filterData: getActivitiesFilter(),
      }),
    );
  }, [historyPage, paymentActivitiesIdentifier]);

  const apiError =
    fetchErrorActivities ||
    openPaymentRequestsFetchError ||
    upcomingPaymentRequestsFetchError;

  React.useEffect(() => {
    if (apiError?.message) {
      enqueueSnackbar(apiError.message, { variant: "error" });
    }
  }, [apiError]);

  function getActivitiesFilter() {
    const sort = {
      field: "datetime",
      direction: "DESC",
    };

    return {
      order: `${sort.field} ${sort.direction}`,
      where: {
        and: compact([
          {
            subtype: {
              inq: [
                ACTIVITY_SUBTYPES.PAYMENT.PAYIN_SUCCEEDED,
                ACTIVITY_SUBTYPES.PAYMENT.PAYOUT_SUCCEEDED,
                ACTIVITY_SUBTYPES.PAYMENT.REQUEST_MANUALLY_PAID,
                ACTIVITY_SUBTYPES.PAYMENT.PAYIN_DISPUTED,
              ],
            },
          },
        ]),
      },
    };
  }

  const fetchMoreHistory = () => setHistoryPage(historyPage + 20);

  const isLoading =
    isFetchingActivities ||
    isFetchingOpenPaymentRequests ||
    isFetchingUpcomingPaymentRequests;

  const isEmpty =
    !isLoading &&
    activities?.length === 0 &&
    upcomingPaymentRequestsCount === 0 &&
    openPaymentRequestsCount === 0;

  return (
    <Page
      title={getLocalizedText("payments.overview.title")}
      isLoading={isLoading}
      isEmpty={isEmpty}
      emptyHeading={getLocalizedText("tenant.payment.empty.heading")}
      emptyMessage={getLocalizedText("tenant.payment.empty.details")}
      moreInfo={getLocalizedText("tenant.no_payment.more_info")}
    >
      <Stack direction="column" gap={2}>
        <OpenPaymentRequests
          count={openPaymentRequestsCount}
          pageCount={Math.ceil(
            openPaymentRequestsCount / CONFIG.DEFAULT_FETCH_LIMIT,
          )}
          page={openPaymentsPage}
          paymentRequests={openPaymentRequests || []}
          isLoading={isLoading}
          handlePayNow={(id: number) => navigate(`/payment/${id}`)}
          handlePageClick={nextPage => setOpenPaymentsPage(nextPage)}
        />
        <UpcomingPaymentRequests
          count={upcomingPaymentRequestsCount}
          pageCount={Math.ceil(upcomingPaymentRequestsCount / 20)}
          page={upcomingPaymentsPage}
          isLoading={isLoading}
          paymentRequests={upcomingPaymentRequests || []}
          handlePayNow={(id: number) => navigate(`/payment/${id}`)}
          handlePageClick={nextPage => setUpcomingPaymentsPage(nextPage)}
        />
        {!downLG && activities && activities?.length > 0 && (
          <PaymentHistoryTable
            activities={activities as IActivityItem[]}
            isLoading={isFetchingActivities}
            fetchMore={fetchMoreHistory}
            activitiesCount={activitiesCount}
          />
        )}
        {downLG && activities && activities?.length > 0 && (
          <PaymentHistory
            activities={activities as IActivityItem[]}
            isLoading={isFetchingActivities}
            fetchMore={fetchMoreHistory}
            activitiesCount={activitiesCount}
          />
        )}
      </Stack>
    </Page>
  );
};
